/* eslint-disable jsx-a11y/no-autofocus --
 * The one autoFocus here moves focus into the inline editor the originator just
 * opened with the rename button. Leaving focus on the button, which has since
 * vanished, would strand keyboard users. The rule is aimed at autofocus on PAGE
 * LOAD, which this is not.
 */
import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Input, Tooltip, Typography } from 'antd';
import { type CSSProperties, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ICON_BTN_PROPS } from '@/components/iconButton';

const { Text } = Typography;

/** Same cap the English label input has always had; the server enforces it on the French one. */
export const ENTRY_LABEL_MAX_LENGTH = 120;

const iconBtn = {
  ...ICON_BTN_PROPS,
  style: { ...ICON_BTN_PROPS.style, width: 26, height: 26, fontSize: 12, borderRadius: 6 },
} as const;

/** The EN / FR row tags the field-properties panel uses beside its bilingual inputs. */
const langTagStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  userSelect: 'none',
};

/**
 * The name of an originator-authored entry — a PDF on the PDF Generation step or a document slot
 * on the Document Upload step — with its optional French name (QA D-31).
 *
 * The English name is required and is what BMO receives as the §14.1 DOCNAME in every language;
 * the French one is display-only and optional (blank = French users see the English name). Both
 * are edited together so a rename can't leave the two out of step.
 */
export function EntryLabelEditor({
  label,
  labelFr,
  editable,
  renameLabel,
  normalize = (s) => s.trim(),
  validate,
  onSave,
  children,
}: {
  label: string;
  labelFr: string | null | undefined;
  editable: boolean;
  /** Accessible name / tooltip for the pencil button. */
  renameLabel: string;
  /** Applied to both names on save (the PDF drawer forces a `.pdf` suffix). Blank stays blank. */
  normalize?: (value: string) => string;
  /**
   * Rejects a proposed English name, returning the message to show, or `null` to allow it.
   *
   * Receives the NORMALIZED name, not the raw draft — in the PDF drawer `normalize` appends the
   * `.pdf` that becomes part of the delivered DOCNAME, so a validator handed the raw draft would
   * read `Void Cheque` as free while a peer holds `Void Cheque.pdf` and then save that collision.
   *
   * Only the English name is checked: the French one is display-only and never reaches BMO.
   */
  validate?: (normalizedEn: string) => string | null;
  /** Persist both names. Resolve `true` to close the editor, `false` to keep it open (the caller
   *  has already reported the failure). */
  onSave: (label: string, labelFr: string | null) => Promise<boolean>;
  /** Shown after the name while not editing (e.g. the "conditional" chip). */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draftEn, setDraftEn] = useState(label);
  const [draftFr, setDraftFr] = useState(labelFr ?? '');
  const [saving, setSaving] = useState(false);

  /**
   * The proposed English name, folded through `normalize` so what is validated is exactly what
   * would be saved.
   */
  const proposedEn = normalize(draftEn);

  /**
   * Only a RENAME is validated — `proposedEn !== label` is the whole rule.
   *
   * An entry that already shares a name with another one keeps saving: the originator can still fix
   * its French name, attach a file (every dropzone action re-posts the English label unchanged), or
   * rename it to something free. The only thing refused is moving a name ONTO a peer's.
   */
  const nameError = proposedEn && proposedEn !== label ? (validate?.(proposedEn) ?? null) : null;

  const open = () => {
    setDraftEn(label);
    setDraftFr(labelFr ?? '');
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setDraftEn(label);
    setDraftFr(labelFr ?? '');
  };

  const commit = async () => {
    const nextEn = normalize(draftEn);
    const frTrimmed = draftFr.trim();
    const nextFr = frTrimmed ? normalize(frTrimmed) : null;
    // English is required — an empty one would also blank the DOCNAME.
    if (!nextEn || (nextEn === label && nextFr === (labelFr?.trim() || null))) {
      cancel();
      return;
    }
    // Deliberately BELOW the no-op return above, so the rule gates the PROPOSED name and never the
    // current one. An entry that already duplicates another (legacy data, or a clone of a workflow
    // that held one) stays fully editable — including its French name, which has nothing to do with
    // the rule — and is fixed by renaming either side. Gating on "is the current state clean" would
    // instead make those entries permanently unsavable.
    if (nameError) return;
    setSaving(true);
    try {
      if (await onSave(nextEn, nextFr)) setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Text strong style={{ fontSize: 14, color: '#111827' }}>
            {label}
          </Text>
          {labelFr?.trim() && (
            <Text type="secondary" style={{ fontSize: 12 }} data-testid="entry-label-fr">
              <span style={{ ...langTagStyle, marginRight: 4 }}>FR</span>
              {labelFr}
            </Text>
          )}
        </div>
        <Tooltip title={renameLabel}>
          <Button
            {...iconBtn}
            aria-label={renameLabel}
            icon={<EditOutlined />}
            disabled={!editable}
            onClick={open}
          />
        </Tooltip>
        {children}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto minmax(0, 320px)',
          columnGap: 6,
          rowGap: 3,
          alignItems: 'center',
          flex: 1,
          minWidth: 0,
        }}
      >
        <span style={langTagStyle}>EN</span>
        <Input
          autoFocus
          size="small"
          value={draftEn}
          disabled={saving}
          maxLength={ENTRY_LABEL_MAX_LENGTH}
          aria-label={t('workflow.steps.entryLabel.enLabel')}
          status={nameError ? 'error' : undefined}
          onChange={(e) => setDraftEn(e.target.value)}
          onPressEnter={() => void commit()}
        />
        {/* Under the English box, in its own column — the name it rejects is the English one, and
            the FR row below must not shift sideways when the message appears. Inline rather than a
            popup, matching BoundedNumberInput on the Document Upload drawer. */}
        {nameError && (
          <Text type="danger" role="alert" style={{ gridColumn: 2, fontSize: 12 }}>
            {nameError}
          </Text>
        )}
        <span style={langTagStyle}>FR</span>
        <Input
          size="small"
          value={draftFr}
          disabled={saving}
          maxLength={ENTRY_LABEL_MAX_LENGTH}
          aria-label={t('workflow.steps.entryLabel.frLabel')}
          placeholder={t('workflow.steps.entryLabel.frPlaceholder')}
          title={t('workflow.steps.entryLabel.frHint')}
          onChange={(e) => setDraftFr(e.target.value)}
          onPressEnter={() => void commit()}
        />
      </div>
      <Tooltip title={t('common.save')}>
        <Button
          {...iconBtn}
          aria-label={t('common.save')}
          type="primary"
          icon={<CheckOutlined />}
          loading={saving}
          disabled={!!nameError}
          onClick={() => void commit()}
        />
      </Tooltip>
      <Tooltip title={t('common.cancel')}>
        <Button
          {...iconBtn}
          aria-label={t('common.cancel')}
          icon={<CloseOutlined />}
          disabled={saving}
          onClick={cancel}
        />
      </Tooltip>
    </div>
  );
}
